import { Document, Page, pdfjs } from 'react-pdf';
import { useState, useEffect, useMemo } from 'react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Button } from "@heroui/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from '@clerk/clerk-react';

// Configure PDF.js worker - use CDN with version from react-pdf to avoid version mismatch
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfPreviewProps {
  fileLog: {
    sharePointFileUrl?: string;
    sharePointFileId?: string;
    file?: File | string | Blob;
  } | string;
  scale?: number;
  height?: string;
}

const PdfPreview = ({ fileLog, scale = 1.2, height = "600px" }: PdfPreviewProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { session } = useSession();

  // Memoize the options
  const options = useMemo(() => ({
    cMapUrl: 'cmaps/',
    cMapPacked: true,
  }), []);

  const PageNavigation = () => (
    <div className="flex items-center justify-center gap-4 py-2">
      <Button
        size="sm"
        disabled={pageNumber <= 1}
        onClick={() => setPageNumber(curr => Math.max(curr - 1, 1))}
      >
        Vorige
      </Button>
      <span>
        Pagina {pageNumber} van {numPages}
      </span>
      <Button
        size="sm"
        disabled={pageNumber >= numPages}
        onClick={() => setPageNumber(curr => Math.min(curr + 1, numPages))}
      >
        Volgende
      </Button>
    </div>
  );

  useEffect(() => {
    let isMounted = true;
    let blobUrl: string | null = null;

    const fetchPdf = async () => {
      try {
        let response;
        if (typeof fileLog === 'string') {
          response = await fetchWithAuth(
            `${API_URL}/graph/file/${fileLog}/content`,
            { method: "GET" },
            session
          );
        } else if (fileLog.sharePointFileId) {
          response = await fetchWithAuth(
            `${API_URL}/graph/file/${fileLog.sharePointFileId}/content`,
            { method: "GET" },
            session
          );
        } else if (fileLog.file) {
          if (fileLog.file instanceof Blob) {
            if (isMounted) setPdfBlob(fileLog.file);
            return;
          }
        }

        if (!response || !isMounted) return;

        const blob = await response.blob();
        const newBlob = new Blob([blob], { type: 'application/pdf' });
        if (isMounted) setPdfBlob(newBlob);
      } catch (err) {
        console.error('Error loading PDF:', err);
        if (isMounted) setError('Failed to load PDF');
      }
    };

    fetchPdf();

    return () => {
      isMounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [(fileLog as any)?.sharePointFileId, session]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const handleOpenInNewTab = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const fileId = typeof fileLog === 'string' ? fileLog : fileLog.sharePointFileId;
    if (fileId) {
      window.open(`${API_URL}/graph/file/${fileId}/content`, '_blank');
    }
  };

  if (error) {
    return (
      <div className="text-center">
        <div className="text-red-500 mb-4">{error}</div>
        <a 
          href="#"
          onClick={handleOpenInNewTab}
          className="text-primary hover:underline"
        >
          Open PDF in new tab
        </a>
      </div>
    );
  }

  if (!pdfBlob) {
    return <div>Loading PDF...</div>;
  }

  return (
    <div className="flex flex-col items-center" style={{ height }}>
      <PageNavigation />
      
      <div className="flex-1 overflow-auto w-full flex justify-center">
        <Document
          file={pdfBlob}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div>Loading PDF...</div>}
          options={options}
        >
          <Page 
            pageNumber={pageNumber} 
            renderTextLayer={true}
            renderAnnotationLayer={true}
            scale={scale}
          />
        </Document>
      </div>
      
      <PageNavigation />
      
      <div className="mt-2">
        <a 
          href="#"
          onClick={handleOpenInNewTab}
          className="text-primary hover:underline"
        >
          Open PDF in new tab
        </a>
      </div>
    </div>
  );
};

export default PdfPreview;